var _AttachFile;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useCallback } from 'react';
import { array, arrayOf, bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { alpha, styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { AttachFile } from 'mosaic-react-icons';
import SovosTypography from '../sovos-typography';
import SovosButton from '../sovos-button';
import FileUploadItem from './components/FileUploadItem';
import SovosSnackbar from '../sovos-snackbar';
import calculateBytes from '../internals/utils/calculateBytes';
import SovosMenuItem from '../sovos-menu-item';
import SovosResizeContainer from '../sovos-resize-container';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const Root = styled(props => /*#__PURE__*/React.createElement(SovosResizeContainer, _extends({
  component: "section"
}, props)))(_ref => {
  let {
    theme: {
      palette,
      shape: themeShape,
      spacing
    }
  } = _ref;
  return {
    backgroundColor: palette.common.white,
    border: "1px solid ".concat(palette.divider),
    borderRadius: themeShape.borderRadius,
    boxSizing: 'content-box',
    position: 'relative',
    width: spacing(50),
    '& .sovosFileUpload': {
      '&__fileInput': {
        border: 0,
        clip: 'rect(0, 0, 0, 0)',
        display: 'block !important',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1
      },
      '&__fileList': {
        borderTop: "1px solid ".concat(palette.divider),
        listStyle: 'none',
        margin: 0,
        maxHeight: spacing(37.5),
        overflowY: 'auto',
        padding: "0 ".concat(spacing())
      }
    }
  };
});
const Dropzone = styled('div')(_ref2 => {
  let {
    theme: {
      palette,
      shape: themeShape,
      spacing
    }
  } = _ref2;
  return {
    alignItems: 'center',
    backgroundColor: alpha(palette.common.white, 0.95),
    border: "2px solid ".concat(palette.common.white),
    borderRadius: themeShape.borderRadius,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    height: spacing(12.5),
    padding: spacing(2),
    '&:focus': {
      outline: 'none'
    },
    '&.sovosFileUpload__dropzone': {
      '&--accepted': {
        borderColor: palette.primary.main,
        color: palette.info.dark,
        justifyContent: 'center'
      },
      '&--disabled': {
        cursor: 'default',
        opacity: 0.5
      }
    },
    '& .sovosFileUpload__dropzone': {
      '&Icon': {
        marginInlineEnd: spacing(2),
        width: spacing(5)
      },
      '&BrowseBtn': {
        marginInlineStart: 'auto'
      }
    }
  };
});
const SovosFileUpload = _ref3 => {
  let {
    acceptedTypes,
    actionsLabels,
    ariaLabel,
    buttonLabel,
    classes,
    className,
    confirmDelete = false,
    defaultErrorActionLabel,
    disabled = false,
    dragActiveLabel,
    FileDeleteConfirmationProps,
    fileList: fileListProp,
    headingLabel,
    helpLabel,
    initialFileList,
    maxSize,
    multiple = true,
    onFileClick,
    onFileDelete,
    onFilesSelected,
    rejectedMessageLabels,
    ...rest
  } = _ref3;
  const {
    t
  } = useMosaicTranslation();
  const [rejected, setRejected] = useState(false);
  const [rejectContent, setRejectContent] = useState((rejectedMessageLabels === null || rejectedMessageLabels === void 0 ? void 0 : rejectedMessageLabels.default) || t('fileUpload.rejectedMessage'));
  const [rejectKey, setRejectKey] = useState(1);
  const [files, setFiles] = useState(initialFileList || []);
  const isControlled = !!fileListProp;
  const fileList = isControlled ? fileListProp : files;
  const getMimeType = val => {
    const type = val.toUpperCase();
    const types = {
      APPLICATION: () => ({
        'application/*': []
      }),
      CSV: () => ({
        'application/vnd.ms-excel': ['.xls', '.csv'],
        'text/csv': ['.csv']
      }),
      DOC: () => ({
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
      }),
      DOCX: () => ({
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
      }),
      EXCEL: () => ({
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
      }),
      IMAGE: () => ({
        'image/': ['.gif', '.jpg', '.jpeg', '.png', '.svg', '.webp', '.avif']
      }),
      JPEG: () => ({
        'image/jpeg': ['.jpg', '.jpeg']
      }),
      JPG: () => ({
        'image/jpeg': ['.jpg', '.jpeg']
      }),
      PNG: () => ({
        'image/png': ['.png']
      }),
      SVG: () => ({
        'image/svg': ['.svg']
      }),
      WEBP: () => ({
        'image/webp': ['.webp']
      }),
      AVIF: () => ({
        'image/avif': ['.avif']
      }),
      PDF: () => ({
        'application/pdf': ['.pdf']
      }),
      TEXT: () => ({
        'text/*': ['.txt']
      }),
      XLS: () => ({
        'application/vnd.ms-excel': ['.xls', '.csv']
      }),
      XLSX: () => ({
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xslx']
      }),
      ZIP: () => ({
        'application/zip': ['.zip']
      })
    };
    if (typeof types[type] !== 'function') {
      return {
        [val]: []
      };
    }
    return types[type]();
  };
  const getAcceptedFileTypes = () => {
    if (typeof acceptedTypes === 'string') {
      return getMimeType(acceptedTypes);
    }
    if (Array.isArray(acceptedTypes)) {
      return acceptedTypes.reduce((accepted, type) => ({
        ...accepted,
        ...getMimeType(type)
      }), {});
    }
    console.error('Expected acceptedTypes to be either a string or array. Received type: ', typeof acceptedTypes);
    return undefined;
  };
  const onDrop = useCallback(acceptedFiles => {
    setFiles(prevFiles => multiple ? [...prevFiles, ...acceptedFiles] : acceptedFiles);
    if (onFilesSelected) {
      onFilesSelected(acceptedFiles);
    }
  }, [multiple, onFilesSelected]);
  const onDropRejected = rejectedFiles => {
    let message = (rejectedMessageLabels === null || rejectedMessageLabels === void 0 ? void 0 : rejectedMessageLabels.default) || t('fileUpload.rejectedMessage');
    const acceptedText = Array.isArray(acceptedTypes) ? acceptedTypes.join(', ') : acceptedTypes;
    rejectedFiles.forEach(_ref4 => {
      let {
        file,
        errors
      } = _ref4;
      const tooLarge = (file === null || file === void 0 ? void 0 : file.size) > maxSize;
      const notAccepted = !getAcceptedFileTypes()[file === null || file === void 0 ? void 0 : file.type] || !(file !== null && file !== void 0 && file.type);
      const notAcceptedLabel = "".concat((rejectedMessageLabels === null || rejectedMessageLabels === void 0 ? void 0 : rejectedMessageLabels.type) || t('fileUpload.rejectedMessageType'), " ").concat(acceptedText, ".");
      const tooLargeLabel = "".concat((rejectedMessageLabels === null || rejectedMessageLabels === void 0 ? void 0 : rejectedMessageLabels.size) || t('fileUpload.rejectedMessageSize'), " ").concat(calculateBytes(maxSize), ".");
      if (errors[0].code === 'duplicate') {
        message = "".concat(message, " ").concat((rejectedMessageLabels === null || rejectedMessageLabels === void 0 ? void 0 : rejectedMessageLabels.duplicate) || t('fileUpload.rejectedMessageDuplicated'));
      }
      if (tooLarge && !message.includes(tooLargeLabel)) {
        message = "".concat(message, " ").concat(tooLargeLabel);
      }
      if (notAccepted && !message.includes(notAcceptedLabel)) {
        message = "".concat(message, " ").concat(notAcceptedLabel);
      }
    });
    setRejectKey(rejectKey + 1);
    setRejectContent(message);
    setRejected(rejectedFiles.length > 0);
  };
  const checkForDuplicates = _ref5 => {
    let {
      path
    } = _ref5;
    if (files.length) {
      if (files.find(file => file.path === path)) {
        return {
          code: 'duplicate',
          message: t('fileUpload.attemptDuplicatedFile')
        };
      }
    }
    return null;
  };
  const closeRejected = useCallback(() => setRejected(false), []);
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open
  } = useDropzone({
    accept: getAcceptedFileTypes(),
    disabled,
    maxSize,
    multiple,
    onDrop,
    onDropRejected,
    useFsAccessApi: false,
    validator: checkForDuplicates
  });
  const fileDelete = !isControlled || onFileDelete ? deletedFile => {
    setFiles(files.filter(file => file.name !== deletedFile.name));
    if (onFileDelete) {
      onFileDelete(deletedFile);
    }
  } : undefined;
  const renderFileList = () => fileList.map(file => {
    const actionItems = !multiple ? [/*#__PURE__*/React.createElement(SovosMenuItem, {
      key: "change",
      onClick: open
    }, (actionsLabels === null || actionsLabels === void 0 ? void 0 : actionsLabels.change) || t('fileUpload.changeAction')), /*#__PURE__*/React.createElement(SovosMenuItem, {
      key: "delete",
      onClick: () => fileDelete(file)
    }, (actionsLabels === null || actionsLabels === void 0 ? void 0 : actionsLabels.delete) || t('fileUpload.deleteAction'))] : undefined;
    return /*#__PURE__*/React.createElement(FileUploadItem, {
      actionMenuItems: actionItems,
      confirmDelete: confirmDelete,
      disabled: disabled,
      FileDeleteConfirmationProps: FileDeleteConfirmationProps,
      fileInfo: file,
      key: file.name,
      onFileClick: onFileClick,
      onFileDelete: multiple ? fileDelete : undefined
    });
  });
  const renderFileDrop = () => {
    if (!multiple && fileList.length) {
      return null;
    }
    if (isDragActive) {
      return /*#__PURE__*/React.createElement(Dropzone, _extends({
        className: clsx('sovosFileUpload__dropzone', 'sovosFileUpload__dropzone--accepted')
      }, getRootProps()), /*#__PURE__*/React.createElement(SovosTypography, {
        variant: "subtitle1",
        color: "primary"
      }, dragActiveLabel || t('fileUpload.dragActive')));
    }
    return /*#__PURE__*/React.createElement(Dropzone, _extends({}, getRootProps(), {
      className: clsx({
        sovosFileUpload__dropzone: true,
        'sovosFileUpload__dropzone--disabled': disabled
      }),
      role: "button"
    }), _AttachFile || (_AttachFile = /*#__PURE__*/React.createElement(AttachFile, {
      className: "sovosFileUpload__dropzoneIcon",
      color: "disabled",
      fontSize: "large"
    })), /*#__PURE__*/React.createElement("div", {
      className: "sovosFileUpload__dropzoneDescription"
    }, /*#__PURE__*/React.createElement(SovosTypography, {
      variant: "h4",
      color: "text.primary"
    }, headingLabel || t('fileUpload.heading')), /*#__PURE__*/React.createElement(SovosTypography, {
      variant: "body1",
      color: "text.secondary"
    }, helpLabel || t('fileUpload.help'))), disabled ? null : /*#__PURE__*/React.createElement(SovosButton, {
      className: "sovosFileUpload__dropzoneBrowseBtn"
    }, buttonLabel || t('fileUpload.button')));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Root, _extends({
    className: clsx('sovosFileUpload', className)
  }, rest), /*#__PURE__*/React.createElement("input", _extends({
    "aria-label": ariaLabel || t('fileUpload.ariaLabel'),
    className: "sovosFileUpload__fileInput"
  }, getInputProps())), renderFileDrop(), fileList.length > 0 && /*#__PURE__*/React.createElement("ul", {
    className: "sovosFileUpload__fileList"
  }, renderFileList())), /*#__PURE__*/React.createElement(SovosSnackbar, {
    defaultErrorActionLabel: defaultErrorActionLabel,
    key: rejectKey,
    length: "medium",
    message: rejectContent,
    onClose: closeRejected,
    open: rejected,
    variant: "error"
  }));
};
SovosFileUpload.propTypes = {
  /**
   * A list of accepted MIME types. See
   * https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
   * for a list valid types. The following abbreviated types will be
   * automatically converted to the correct MIME type: `application`,
   * `csv`, `doc`, `docx`, `excel`, `image`, `jpeg`, `jpg`, `pdf`, `png`,
   * `text`, `tiff`, `xls`, `xlsx, `zip`
   */
  acceptedTypes: oneOfType([array, string]).isRequired,
  /**
   * Labels for single select file actions
   */
  actionsLabels: shape({
    change: string,
    delete: string
  }),
  /**
   * ARIA label for the component
   */
  ariaLabel: string,
  /**
   * Select file button label
   */
  buttonLabel: string,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Add Confirmation Dialog before file delete function is fired
   */
  confirmDelete: bool,
  /**
   * Error snackbar action button label
   */
  defaultErrorActionLabel: string,
  /**
   * If true, component is disabled
   */
  disabled: bool,
  /**
   * Message displayed when user is dragging file into the browser
   */
  dragActiveLabel: string,
  /**
   * Confirmation Dialog props: defaultTitleText, defaultContentText,
   * defaultActionButtonLabel, defaultCancelButtonLabel
   */
  FileDeleteConfirmationProps: shape({
    defaultTitleText: string,
    defaultContentText: string,
    defaultActionButtonLabel: string,
    defaultCancelButtonLabel: string
  }),
  /**
   * List of uploaded or uploading files
   */
  fileList: arrayOf(shape({
    name: string,
    bytes: number,
    progress: shape({
      loading: bool,
      percent: number
    }),
    uploadedDate: string,
    uploadedBy: string
  })),
  /**
   * Upload heading
   */
  headingLabel: string,
  /**
   * Upload help text
   */
  helpLabel: string,
  /**
   * List of uploaded files used for uncontrolled state
   */
  initialFileList: arrayOf(shape({
    name: string,
    bytes: number,
    progress: shape({
      loading: bool,
      percent: number
    }),
    uploadedDate: string,
    uploadedBy: string
  })),
  /**
   * Maximum file upload size in Bytes
   */
  maxSize: number,
  /**
   * Allow multiple files to be selected and uploaded. When false, only a
   * single file will be allowed.
   */
  multiple: bool,
  /**
   * Callback fired when the user clicks a file in the upload list
   */
  onFileClick: func,
  /**
   * Callback fired when the user clicks file delete button
   * `function(fileInfo: object) => void`
   */
  onFileDelete: func,
  /**
   * Callback fired when the user selects or drops a file(s)
   * `function(acceptedFiles: object[]) => void`
   */
  onFilesSelected: func,
  /**
   * Messages shown when a file selected for upload fails to meet upload requirements
   */
  rejectedMessageLabels: shape({
    default: string,
    duplicate: string,
    type: string,
    size: string
  })
};
export default SovosFileUpload;